import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaUser, FaBars, FaTimes, FaSignOutAlt, FaTachometerAlt, FaClipboardList, FaGift, FaChevronDown, FaHistory } from 'react-icons/fa';

const ResponsiveNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setProfileDropdownOpen(false);
  }, [location.pathname]);

  // Handle keyboard navigation
  const handleKeyDown = (event, action) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
    if (event.key === 'Escape') {
      setIsOpen(false);
      setProfileDropdownOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setProfileDropdownOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  const toggleMobileMenu = () => {
    setIsOpen(!isOpen);
    setProfileDropdownOpen(false);
  };

  const toggleProfileDropdown = () => {
    setProfileDropdownOpen(!profileDropdownOpen);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm py-3' : 'bg-white py-4 border-b border-gray-100'
        }`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="container-responsive">
        <div className="flex justify-between items-center px-4 md:px-6">
          {/* Logo */}
          <Link
            to={user ? '/dashboard' : '#'}
            className="flex items-center group transition-transform duration-300 hover:scale-[1.02] ml-1 md:ml-2"
            aria-label="Elcid Research - Go to homepage"
            onClick={(e) => !user && e.preventDefault()}
          >
            <img
              src={`${process.env.PUBLIC_URL || ''}/erasebg-elicit.png`}
              alt="Elcid Research Logo"
              className="h-12 sm:h-14 w-auto transition-all duration-300 group-hover:drop-shadow-sm"
              onError={(e) => { e.currentTarget.src = `${process.env.PUBLIC_URL || ''}/elcid-removebg-preview.png`; }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-2 md:space-x-3">
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-all duration-300 font-semibold text-sm md:text-base ${isActive('/dashboard')
                      ? 'bg-green-50 text-green-700 font-bold'
                      : 'text-slate-600 hover:text-green-700 hover:bg-green-50/50'
                    }`}
                  aria-current={isActive('/dashboard') ? 'page' : undefined}
                >
                  <FaTachometerAlt className={isActive('/dashboard') ? 'text-green-600' : 'text-slate-400'} aria-hidden="true" />
                  <span>Dashboard</span>
                </Link>
                <Link
                  to="/surveys"
                  className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-all duration-300 font-semibold text-sm md:text-base ${isActive('/surveys')
                      ? 'bg-green-50 text-green-700 font-bold'
                      : 'text-slate-600 hover:text-green-700 hover:bg-green-50/50'
                    }`}
                  aria-current={isActive('/surveys') ? 'page' : undefined}
                >
                  <FaClipboardList className={isActive('/surveys') ? 'text-green-600' : 'text-slate-400'} aria-hidden="true" />
                  <span>Surveys</span>
                </Link>
                <Link
                  to="/rewards"
                  className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-all duration-300 font-semibold text-sm md:text-base ${isActive('/rewards')
                      ? 'bg-green-50 text-green-700 font-bold'
                      : 'text-slate-600 hover:text-green-700 hover:bg-green-50/50'
                    }`}
                  aria-current={isActive('/rewards') ? 'page' : undefined}
                >
                  <FaGift className={isActive('/rewards') ? 'text-green-600' : 'text-slate-400'} aria-hidden="true" />
                  <span>Rewards</span>
                </Link>

                {/* Profile Dropdown */}
                <div className="relative ml-2" ref={dropdownRef}>
                  <button
                    onClick={toggleProfileDropdown}
                    onKeyDown={(e) => handleKeyDown(e, toggleProfileDropdown)}
                    className={`flex items-center space-x-3 pl-3 pr-2 py-1.5 rounded-full border transition-all duration-200 outline-none focus:ring-2 focus:ring-green-500/20 ${profileDropdownOpen
                        ? 'bg-green-50 border-green-200 ring-2 ring-green-100'
                        : 'bg-white border-gray-200 hover:border-green-300 hover:bg-gray-50'
                      }`}
                    aria-expanded={profileDropdownOpen}
                    aria-haspopup="true"
                    aria-label={`User menu for ${user.firstName}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-green-700 text-white flex items-center justify-center text-xs font-bold shadow-sm">
                      <FaUser aria-hidden="true" />
                    </div>
                    <span className="hidden xl:inline text-sm md:text-base font-semibold text-slate-700">{user.firstName}</span>
                    <FaChevronDown
                      className={`text-xs text-slate-400 transition-transform duration-300 ${profileDropdownOpen ? 'rotate-180' : ''}`}
                      aria-hidden="true"
                    />
                  </button>

                  {profileDropdownOpen && (
                    <div
                      className="absolute right-0 mt-3 w-56 bg-white rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] border border-gray-100 py-2 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
                      role="menu"
                      aria-orientation="vertical"
                    >
                      <div className="px-4 py-3 border-b border-gray-50 bg-gray-50/50">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Signed in as</p>
                        <p className="text-sm font-bold text-slate-800 truncate">{user.email}</p>
                      </div>

                      <div className="py-1">
                        <Link
                          to="/profile"
                          className="flex items-center space-x-3 px-4 py-2.5 text-slate-600 hover:text-green-700 hover:bg-green-50/50 transition-colors duration-200"
                          onClick={() => setProfileDropdownOpen(false)}
                          role="menuitem"
                        >
                          <FaUser className="text-slate-400 group-hover:text-green-500" aria-hidden="true" />
                          <span className="font-medium text-sm">Profile Settings</span>
                        </Link>
                        <Link
                          to="/payment-history"
                          className="flex items-center space-x-3 px-4 py-2.5 text-slate-600 hover:text-green-700 hover:bg-green-50/50 transition-colors duration-200"
                          onClick={() => setProfileDropdownOpen(false)}
                          role="menuitem"
                        >
                          <FaHistory className="text-slate-400 group-hover:text-green-500" aria-hidden="true" />
                          <span className="font-medium text-sm">Payment History</span>
                        </Link>
                      </div>

                      <div className="border-t border-gray-100 py-1">
                        <button
                          onClick={handleLogout}
                          className="flex items-center space-x-3 px-4 py-2.5 text-red-600 hover:bg-red-50 transition-colors duration-200 w-full text-left"
                          role="menuitem"
                        >
                          <FaSignOutAlt aria-hidden="true" />
                          <span className="font-medium text-sm">Sign out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-3">
                <Link
                  to="/login"
                  className={`px-5 py-2 rounded-full transition-all duration-300 font-semibold text-sm md:text-base ${isActive('/login')
                      ? 'bg-green-50 text-green-700'
                      : 'text-slate-600 hover:text-green-700 hover:bg-green-50/50'
                    }`}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="px-6 py-2 rounded-full font-semibold text-sm text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 shadow-lg shadow-green-200 transition-all duration-300 transform hover:scale-105 hover:-translate-y-0.5"
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={toggleMobileMenu}
            onKeyDown={(e) => handleKeyDown(e, toggleMobileMenu)}
            className="lg:hidden text-slate-700 hover:text-green-700 transition-colors duration-200 p-2 rounded-lg hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500/20"
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
          >
            {isOpen ? <FaTimes size={24} aria-hidden="true" /> : <FaBars size={24} aria-hidden="true" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div
          id="mobile-menu"
          className={`lg:hidden fixed inset-x-0 top-[88px] bg-white border-t border-gray-100 shadow-xl transition-all duration-300 ease-in-out overflow-hidden z-40 ${isOpen ? 'max-h-[calc(100vh-88px)] opacity-100' : 'max-h-0 opacity-0'
            }`}
          ref={mobileMenuRef}
          role="menu"
          aria-orientation="vertical"
        >
          <div className="container-responsive py-6 px-4 space-y-2 h-full overflow-y-auto">
            {user ? (
              <>
                <div className="flex items-center space-x-4 mb-6 p-4 bg-green-50 rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-700 text-white flex items-center justify-center text-lg font-bold shadow-md">
                    <FaUser aria-hidden="true" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800 text-lg">{user.firstName} {user.lastName}</p>
                    <p className="text-sm text-slate-500">{user.email}</p>
                  </div>
                </div>

                <Link
                  to="/dashboard"
                  className={`flex items-center space-x-3 px-4 py-3.5 rounded-xl transition-all duration-200 font-medium ${isActive('/dashboard')
                      ? 'bg-green-100 text-green-800 font-bold'
                      : 'text-slate-600 hover:bg-gray-50 hover:text-green-700'
                    }`}
                  role="menuitem"
                >
                  <FaTachometerAlt className={isActive('/dashboard') ? 'text-green-600' : 'text-slate-400'} aria-hidden="true" />
                  <span>Dashboard</span>
                </Link>
                <Link
                  to="/surveys"
                  className={`flex items-center space-x-3 px-4 py-3.5 rounded-xl transition-all duration-200 font-medium ${isActive('/surveys')
                      ? 'bg-green-100 text-green-800 font-bold'
                      : 'text-slate-600 hover:bg-gray-50 hover:text-green-700'
                    }`}
                  role="menuitem"
                >
                  <FaClipboardList className={isActive('/surveys') ? 'text-green-600' : 'text-slate-400'} aria-hidden="true" />
                  <span>Surveys</span>
                </Link>
                <Link
                  to="/rewards"
                  className={`flex items-center space-x-3 px-4 py-3.5 rounded-xl transition-all duration-200 font-medium ${isActive('/rewards')
                      ? 'bg-green-100 text-green-800 font-bold'
                      : 'text-slate-600 hover:bg-gray-50 hover:text-green-700'
                    }`}
                  role="menuitem"
                >
                  <FaGift className={isActive('/rewards') ? 'text-green-600' : 'text-slate-400'} aria-hidden="true" />
                  <span>Rewards & Points</span>
                </Link>

                <hr className="border-gray-100 my-4" />

                <Link
                  to="/profile"
                  className="flex items-center space-x-3 px-4 py-3.5 rounded-xl text-slate-600 hover:bg-gray-50 hover:text-green-700 transition-all duration-200 font-medium"
                  role="menuitem"
                >
                  <FaUser className="text-slate-400" aria-hidden="true" />
                  <span>My Profile</span>
                </Link>
                <Link
                  to="/payment-history"
                  className="flex items-center space-x-3 px-4 py-3.5 rounded-xl text-slate-600 hover:bg-gray-50 hover:text-green-700 transition-all duration-200 font-medium"
                  role="menuitem"
                >
                  <FaHistory className="text-slate-400" aria-hidden="true" />
                  <span>Payment History</span>
                </Link>

                <div className="pt-4 mt-4">
                  <button
                    onClick={handleLogout}
                    className="flex items-center justify-center space-x-2 w-full px-4 py-3.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors duration-200 font-bold"
                    role="menuitem"
                  >
                    <FaSignOutAlt aria-hidden="true" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-col space-y-3 p-4">
                <Link
                  to="/login"
                  className="flex justify-center w-full px-4 py-3 rounded-xl border border-gray-200 font-semibold text-slate-700 hover:bg-gray-50 hover:text-green-700 transition-all duration-200"
                  role="menuitem"
                >
                  Log In
                </Link>
                <Link
                  to="/register"
                  className="flex justify-center w-full px-4 py-3 rounded-xl bg-gradient-to-r from-green-600 to-green-700 text-white font-bold shadow-lg shadow-green-200 hover:from-green-500 hover:to-green-600 transition-all duration-200"
                  role="menuitem"
                >
                  Get Started Free
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default ResponsiveNavbar;
